import ReactModal from 'react-modal';
import { useWindowHeight } from '@react-hook/window-size';
import { useElementSize } from '@custom-react-hooks/use-element-size';

import { getClasses } from '@/utils/helpers';

type Props = {
  title: string;
  isOpen: boolean;
  handleClose: () => void;
  children: any;
};

const customStyles: ReactModal.Styles = {
  overlay: {
    background: 'rgba(0,0,0, 0.3)',
    backdropFilter: 'blur(2px)',
    zIndex: 9999,
    position: 'fixed',
  },
};

export default function Modal({ title, isOpen, handleClose, children }: Props) {
  const windowHeight = useWindowHeight();
  const [setRef, size] = useElementSize();

  /**
   * There's a bug in react-modal where the top of the modal is truncated
   * when the height of the modal exceeds the height of the window.
   *
   * To fix this, we only center the modal vertically when the height of
   * the modal is less than the height of the window.
   */
  const shouldCenterVertically = size.height < windowHeight;

  return (
    <ReactModal
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={customStyles}
      className="overflow-y-scroll h-full p-4"
      contentLabel={`${title} Modal`}
    >
      <div className={getClasses('flex justify-center', shouldCenterVertically ? 'items-center h-full' : '')}>
        {/* card */}
        <div className=" bg-white rounded-lg shadow max-w-4xl w-full" ref={setRef}>
          {/* header */}
          <div className="flex justify-between items-center p-5 rounded-t border-b">
            <h3 className="text-xl font-medium text-gray-900">{title}</h3>
            <button
              type="button"
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
              onClick={handleClose}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </button>
          </div>

          {/* body / content */}
          <div className="p-6 space-y-6">
            <div className="leading-relaxed">{children}</div>
          </div>
        </div>
      </div>
    </ReactModal>
  );
}
